/**
 * 🗨️ battleBanter.js — Tier-based pet/CPU one-liners that pop above
 * fighters during battles. Pure frontend, no backend changes.
 *
 * Trigger surfaces:
 *  - On fight start (intro)
 *  - After landing a crit (gloat)
 *  - After taking a crit (ow)
 *  - When staggered (dizzy)
 *  - Idle between turns (flex)
 *  - On victory / defeat
 *
 * Bank selection by personality (CPU) or tier (player pet).
 */

const PERSONALITY_BANK = {
  cocky: {
    intro:  ["Try not to cry. 😏", "Hope you stretched.", "Easy money."],
    gloat:  ["That all you got?", "Skill issue.", "Stay down, kiddo."],
    ow:     ["Lucky shot…", "Won't happen twice.", "…fine, I felt that."],
    stagger:["Wh— wait…", "Stop the room…", "I see two of you."],
    idle:   ["Move already!", "*flexes*", "Bored yet?"],
    win:    ["Told you.", "Next!", "Easy."],
    loss:   ["Lucky day for ya.", "We'll see about this.", "Catch me later."],
  },
  fury: {
    intro:  ["RAAARGH!", "I will end you.", "Burn it all."],
    gloat:  ["FEEL IT!", "Yes! YES!", "More! MORE!"],
    ow:     ["GRAAH!", "You'll PAY for that!", "Pain only fuels me."],
    stagger:["…what…", "*growls*", "No no no…"],
    idle:   ["Fight back!", "Don't you DARE flinch.", "*snarls*"],
    win:    ["DOMINATION.", "Crushed.", "Bow before me."],
    loss:   ["…impossible…", "I'll be back.", "This isn't over."],
  },
  stalker: {
    intro:  ["You won't see me coming.", "*silent stare*", "Tick. Tock."],
    gloat:  ["Predictable.", "I expected more.", "Hush, now."],
    ow:     ["…clever.", "Interesting.", "Hmm."],
    stagger:["I... slipped.", "Where…?", "*blink*"],
    idle:   ["Watching you.", "Your move.", "Patience…"],
    win:    ["As expected.", "Goodnight.", "Quiet now."],
    loss:   ["You earned this one.", "Worthy.", "Until next time."],
  },
  wraith: {
    intro:  ["Embrace the dark.", "You shouldn't have come.", "*whispers*"],
    gloat:  ["Withered.", "All things end.", "Hopeless, isn't it?"],
    ow:     ["The veil parts…", "Curious.", "You touched me."],
    stagger:["…the world spins…", "Cold…", "*static*"],
    idle:   ["Eternal.", "I outlast you all.", "Echoes…"],
    win:    ["Silence reclaims you.", "Sleep.", "Dust to dust."],
    loss:   ["Light… returns.", "I yield.", "Until shadow falls again."],
  },
  balanced: {
    intro:  ["Let's do this.", "Good luck out there.", "Bring it on."],
    gloat:  ["Got 'em!", "Nice one.", "💢"],
    ow:     ["Ouch!", "Tch.", "Got me."],
    stagger:["*wobbles*", "Dizzy…", "Hold on…"],
    idle:   ["You good?", "Steady…", "💪"],
    win:    ["GG!", "Nice fight!", "🏆"],
    loss:   ["Good fight.", "I'll train harder.", "Respect."],
  },
  defensive: {
    intro:  ["I won't fold.", "Steady…", "Try me."],
    gloat:  ["Counter!", "Like a wall.", "Solid."],
    ow:     ["Tch — guarded.", "Held it.", "Barely."],
    stagger:["*staggers*", "Footing…", "Hold the line…"],
    idle:   ["Ready when you are.", "Centered.", "🛡️"],
    win:    ["Patience wins.", "Held the line.", "Discipline."],
    loss:   ["You broke through.", "Well earned.", "Respect."],
  },
  aggressive: {
    intro:  ["No mercy!", "LET'S GO!", "All in!"],
    gloat:  ["BOOM!", "Crushing it!", "🔥🔥🔥"],
    ow:     ["Grr!", "Just a scratch!", "Tch!"],
    stagger:["*wobble*", "Wait what—", "Stop spinning!"],
    idle:   ["Come ON!", "Move!", "*paces*"],
    win:    ["VICTORY!", "DUSTED!", "🏆💥"],
    loss:   ["…that's a first.", "Rematch?", "GG."],
  },
};

// 🐦 Player pet tier-flavored intro (gets bolted onto a balanced bank)
const TIER_INTROS = {
  egg:              ["*peep peep*", "🥚 ready!", "Squeak."],
  chick:            ["I've trained for this!", "*chirp*", "Watch this!"],
  fledgling:        ["My wings are stronger now.", "I belong here.", "Let's fly."],
  phoenix:          ["I rise again.", "Burn bright.", "🔥 ready."],
  inferno_phoenix:  ["INFERNO awaits.", "I am the flame.", "🌋"],
};

export function getBanterBank(personality) {
  return PERSONALITY_BANK[personality] || PERSONALITY_BANK.balanced;
}

export function getPetIntro(tier) {
  const lines = TIER_INTROS[tier] || TIER_INTROS.egg;
  return lines[Math.floor(Math.random() * lines.length)];
}

export function pickBanter(personality, surface) {
  const bank = getBanterBank(personality);
  const lines = bank[surface] || bank.idle;
  return lines[Math.floor(Math.random() * lines.length)];
}

/** Compute the banter line for the most recent log entry, if any. */
export function banterFromLogEntry(entry, who, personality, tier) {
  // who = 'pet' or 'cpu', from the perspective of which fighter this is
  // entry = battle log entry
  if (!entry) return null;
  if (entry.type === "attack") {
    if (entry.actor === who && entry.crit) return pickBanter(personality, "gloat");
    if (entry.actor !== who && entry.crit) return pickBanter(personality, "ow");
    return null; // suppress banter on normal hits to avoid spam
  }
  if (entry.type === "stagger_apply" && entry.actor !== who) {
    // who got staggered (target = opposite of actor)
    return pickBanter(personality, "stagger");
  }
  if (entry.type === "ultimate" && entry.actor === who) {
    return pickBanter(personality, "gloat");
  }
  if (entry.type === "ko" && entry.actor === who) {
    return pickBanter(personality, "ow");
  }
  return null;
}
